import { apiGet, apiSend, apiFetch } from "../../lib/api";

export type ChatStats = {
  totalMessages: number;
  unansweredMessages: number;
  todayMessages: number;
};

export type ChatMessage = {
  id: number;
  client_id: string;
  session_id: string | null;
  user_message: string;
  bot_response: string | null;
  was_answered: boolean | null;
  created_at: string;
};

export type ChatConversationsResponse = {
  rows: ChatMessage[];
  total: number;
  limit: number;
  offset: number;
};

export type ChatHealth = {
  status: "healthy" | "degraded" | "unreachable" | "unconfigured";
  httpStatus?: number;
  error?: string;
  /** Present only when degraded — the upstream body explaining why. */
  detail?: string;
};

export type KnowledgeChunk = {
  id: number;
  content: string;
  source_url: string | null;
  created_at: string;
  updated_at: string | null;
};

export type KnowledgeChunksResponse = {
  rows: KnowledgeChunk[];
  total: number;
  limit: number;
  offset: number;
};

export function fetchChatStats() {
  return apiGet<ChatStats>("/chat/stats");
}

export function fetchChatConversations(params?: {
  limit?: number;
  offset?: number;
  answered?: string;
}) {
  const q = new URLSearchParams();
  if (params?.limit) q.set("limit", String(params.limit));
  if (params?.offset) q.set("offset", String(params.offset));
  if (params?.answered) q.set("answered", params.answered);
  const qs = q.toString();
  return apiGet<ChatConversationsResponse>(`/chat/conversations${qs ? `?${qs}` : ""}`);
}

export function fetchChatSession(sessionId: string) {
  return apiGet<ChatMessage[]>(`/chat/conversations/${encodeURIComponent(sessionId)}`);
}

export function fetchChatHealth() {
  return apiGet<ChatHealth>("/chat/health");
}

// ─── KB CRUD ──────────────────────────────────────────────────────

export function fetchKnowledgeChunks(params?: {
  limit?: number;
  offset?: number;
  search?: string;
}) {
  const q = new URLSearchParams();
  if (params?.limit) q.set("limit", String(params.limit));
  if (params?.offset) q.set("offset", String(params.offset));
  if (params?.search) q.set("search", params.search);
  const qs = q.toString();
  return apiGet<KnowledgeChunksResponse>(`/chat/kb${qs ? `?${qs}` : ""}`);
}

export function createKnowledgeChunk(content: string) {
  return apiSend<KnowledgeChunk>("/chat/kb", "POST", { content });
}

export function updateKnowledgeChunk(id: number, content: string) {
  return apiSend<KnowledgeChunk>(`/chat/kb/${id}`, "PATCH", { content });
}

export async function deleteKnowledgeChunk(id: number) {
  const r = await apiFetch(`/chat/kb/${id}`, { method: "DELETE" });
  if (!r.ok) throw new Error(`${r.status}: ${await r.text()}`);
  return (await r.json()) as { ok: boolean };
}

// ─── KB AI Draft ──────────────────────────────────────────────────

export type KbDraft = {
  category: string;
  questions: string[];
  answer: string;
  content: string;
};

export function draftKbEntry(question: string, answer: string) {
  return apiSend<KbDraft>("/chat/kb/draft", "POST", { question, answer });
}

// ─── Conversation patch ───────────────────────────────────────────

export function patchConversation(id: number, data: { was_answered: boolean }) {
  return apiSend<ChatMessage>(`/chat/conversations/${id}`, "PATCH", data);
}
